"use client";

import { useState } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import ExportFilters from "./ExportFilters";
import ExportColumns from "./ExportColumns";
import ExportSummary from "./ExportSummary";
import ExportActions from "./ExportActions";
import { apiClient } from "../../services/api";
import ExportResultTable, { ExportNetworkUser } from "./ExportResultTable";

const ExportExcel = () => {
    const [mode] = useState<"level" | "rank">("level");
    const [isDownloading, setIsDownloading] = useState(false);

    const [level, setLevel] = useState("");
    const [status, setStatus] = useState("");
    const [rank, setRank] = useState("");
    const [search, setSearch] = useState("");
    const [month, setMonth] = useState(
        String(new Date().getMonth() + 1).padStart(2, "0")
    );
    const [year, setYear] = useState(
        new Date().getFullYear().toString()
    );

    const [showExportModal, setShowExportModal] = useState(false);

    const [selectedColumns, setSelectedColumns] = useState<string[]>([
        "code",
        "status",
        "royalties",
        "bonus",
        "rank",
        "registered",
        "active"
    ]);

    const [users, setUsers] = useState<ExportNetworkUser[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const buildParams = () => {
        const params = new URLSearchParams();

        if (search) params.set("search", search);

        if (level) params.set("level", level);

        if (rank) params.set("rank_name", rank);

        // El backend espera "true"/"false", no "active"/"inactive".
        if (status) params.set("loyalty_status", status === "active" ? "true" : "false");

        if (month) params.set("month", month);

        if (year) params.set("year", year);

        if (selectedColumns.length) {
            params.set("columns", selectedColumns.join(","));
        }

        return params;
    };

    const validateFilters = () => {

        if (!month || !year) {
            alert("Debes seleccionar un período.");
            return false;
        }

        const hasFilters =
            search.trim() ||
            level ||
            rank ||
            status;

        if (!hasFilters) {
            alert("Debes diligenciar al menos un filtro.");
            return false;
        }

        if (!selectedColumns.length) {
            alert("Debes seleccionar al menos una columna.");
            return false;
        }

        return true;
    };


    const handleGenerateExcel = async () => {

        if (!validateFilters()) return;

        try {

            setIsDownloading(true);

            const response = await apiClient.get(
                `/users/network/report?${buildParams().toString()}`,
                {
                    responseType: "blob",
                    headers: {
                        Authorization: localStorage.getItem("token") || "",
                    },
                }
            );

            const url = window.URL.createObjectURL(
                new Blob([response.data])
            );

            const link = document.createElement("a");

            link.href = url;
            link.download = "network-report.xlsx";

            document.body.appendChild(link);

            link.click();

            link.remove();

            window.URL.revokeObjectURL(url);

        } finally {

            setIsDownloading(false);

        }

    };

    const handleSearch = async () => {

        if (!validateFilters()) return;

        try {

            setIsSearching(true);

            const response = await apiClient.get(
                `/users/network/search?${buildParams().toString()}`,
                {
                    headers: {
                        Authorization: localStorage.getItem("token") || "",
                    },
                }
            );

            setUsers(response.data.data.users);
        } finally {

            setIsSearching(false);

        }
    };


    return (
        <>
            <Row className="g-4">
                <Col lg={12}>
                    <ExportFilters
                        level={level}
                        setLevel={setLevel}
                        status={status}
                        setStatus={setStatus}
                        rank={rank}
                        setRank={setRank}
                        search={search}
                        setSearch={setSearch}
                        month={month}
                        setMonth={setMonth}
                        year={year}
                        setYear={setYear}
                        onGenerateExcel={() => setShowExportModal(true)}
                        onSearch={handleSearch}
                    />
                </Col>

                <Col lg={12} className="mt-3">
                    <div className="default-table-area">
                        <ExportResultTable
                            teamMembers={users}
                            isLoading={isSearching}
                        />
                    </div>

                </Col>

                <Modal
                    show={showExportModal}
                    onHide={() => setShowExportModal(false)}
                    size="xl"
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Configurar exportación
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>

                        <Row className="g-4">

                            <Col lg={9}>
                                <ExportColumns
                                    selectedColumns={selectedColumns}
                                    setSelectedColumns={setSelectedColumns}
                                />
                            </Col>

                            <Col lg={3}>
                                <ExportSummary
                                    mode={mode}
                                    level={level}
                                    status={status}
                                    rank={rank}
                                    selectedColumns={selectedColumns}
                                />
                            </Col>

                            <Col lg={12}>
                                <ExportActions
                                    handleGenerateExcel={handleGenerateExcel}
                                    isDownloading={isDownloading}
                                />
                            </Col>

                        </Row>

                    </Modal.Body>
                </Modal>

            </Row>

        </>
    );
};

export default ExportExcel;